import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
// TawkToChat removed
import { RestaurantProvider } from "@/contexts/RestaurantContext";
import { CookieConsent } from "@/components/CookieConsent";
import LoadingLogo from "@/components/LoadingLogo";

// Critical pages - loaded immediately
import SplitChoicePage from "./pages/SplitChoicePage";
import SimpleAuth from "./pages/SimpleAuth";
import MenuView from "./pages/MenuView";
import NotFound from "./pages/NotFound";

// Lazy loaded pages - Dashboard
const MainDashboard = lazy(() => import("./pages/MainDashboard"));
const HotelDashboard = lazy(() => import("./pages/HotelDashboard"));
const HotelMenuCreation = lazy(() => import("./pages/HotelMenuCreation"));
const HotelLinktree = lazy(() => import("./pages/HotelLinktree"));
const Test5 = lazy(() => import("./pages/Test5"));
const HotelAnalytics = lazy(() => import("./pages/HotelAnalytics"));
const MenusPage = lazy(() => import("./pages/MenusPage"));
const MenuEditor = lazy(() => import("./pages/MenuEditor"));
const MenuWizard = lazy(() => import("./pages/MenuWizard"));
const MenuOCR = lazy(() => import("./pages/MenuOCR"));
const OCRMenuSetup = lazy(() => import("./pages/OCRMenuSetup"));
const HappyHour = lazy(() => import("./pages/HappyHour"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Reservations = lazy(() => import("./pages/Reservations"));
const RestaurantSettings = lazy(() => import("./pages/RestaurantSettings"));
const HotelSettings = lazy(() => import("./pages/HotelSettings"));
const CampingSettings = lazy(() => import("./pages/CampingSettings"));
const QrCodes = lazy(() => import("./pages/QrCodes"));
const Marketing = lazy(() => import("./pages/Marketing"));
const Translation = lazy(() => import("./pages/Translation"));
const SupportTickets = lazy(() => import("./pages/SupportTickets"));

// Lazy loaded pages - Onboarding
const Onboarding = lazy(() => import("./pages/Onboarding"));
const AddRestaurant = lazy(() => import("./pages/AddRestaurant"));
const RapidOnboarding = lazy(() => import("./pages/RapidOnboarding"));
const UltraFastOnboarding = lazy(() => import("./pages/UltraFastOnboarding"));
const QuickOCROnboarding = lazy(() => import("./pages/QuickOCROnboarding"));
const MagicOnboarding = lazy(() => import("./pages/MagicOnboarding"));
const SetupRestaurant = lazy(() => import("./pages/SetupRestaurant"));
const QuickSignup = lazy(() => import("./pages/QuickSignup"));
const MenuPreview = lazy(() => import("./pages/MenuPreview"));
const Success = lazy(() => import("./pages/Success"));

// Lazy loaded pages - Marketing & Landing
const NewLanding = lazy(() => import("./pages/NewLanding"));
const LandingV2 = lazy(() => import("./pages/LandingV2"));
const SaasShowcase = lazy(() => import("./pages/SaasShowcase"));
const RestaurantOwnerLanding = lazy(() => import("./pages/RestaurantOwnerLanding"));
const ConversionLanding = lazy(() => import("./pages/ConversionLanding"));
const LPOCRLanding = lazy(() => import("./pages/LPOCRLanding"));
const DemoOCR = lazy(() => import("./pages/DemoOCR"));
const Gemini = lazy(() => import("./pages/Gemini"));
const PricingPage = lazy(() => import("./pages/PricingPage"));

// Lazy loaded pages - Static/Info
const Features = lazy(() => import("./pages/Features"));
const Solutions = lazy(() => import("./pages/Solutions"));
const SmallRestaurants = lazy(() => import("./pages/SmallRestaurants"));
const Chains = lazy(() => import("./pages/Chains"));
const FineDining = lazy(() => import("./pages/FineDining"));
const CafesAndBars = lazy(() => import("./pages/CafesAndBars"));
const Support = lazy(() => import("./pages/Support"));
const Documentation = lazy(() => import("./pages/Documentation"));
const Terms = lazy(() => import("./pages/Terms"));
const Cgv = lazy(() => import("./pages/Cgv"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Pizza = lazy(() => import("./pages/Pizza"));
const Bar = lazy(() => import("./pages/Bar"));
const RestaurantMap = lazy(() => import("./pages/RestaurantMap"));
const GuideGoogleMaps = lazy(() => import("./pages/GuideGoogleMaps"));

// Lazy loaded pages - Legacy
const Index = lazy(() => import("./pages/Index"));
const PaymentRequired = lazy(() => import("./pages/PaymentRequired"));
const SharedMenuView = lazy(() => import("./pages/SharedMenuView"));
const ClaimPrize = lazy(() => import("./pages/ClaimPrize").then(m => ({ default: m.ClaimPrize })));
const CancelReservation = lazy(() => import("./pages/CancelReservation"));
const ManageReservation = lazy(() => import("./pages/ManageReservation"));
const ConfirmReservation = lazy(() => import("./pages/ConfirmReservation"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const RestaurantSite = lazy(() => import("./pages/RestaurantSite"));
const ActivateHelp = lazy(() => import("./pages/ActivateHelp"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));
const ClaimAccount = lazy(() => import("./pages/ClaimAccount"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Lazy loaded pages - Admin
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminSupport = lazy(() => import("./pages/admin/AdminSupport"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminLeadsCRM = lazy(() => import("./pages/admin/AdminLeadsCRM"));
const AdminTickets = lazy(() => import("./pages/admin/AdminTickets"));
const AdminLeadsImport = lazy(() => import("./pages/admin/AdminLeadsImport"));
const AdminFunnel = lazy(() => import("./pages/admin/AdminFunnel"));
const AdminOCR = lazy(() => import("./pages/admin/AdminOCR"));
const LandingDuplicate = lazy(() => import("./pages/LPCopy"));
const HotelsLanding = lazy(() => import("./pages/HotelsLanding"));
const CampingsLanding = lazy(() => import("./pages/CampingsLanding"));
const HotelOnboarding = lazy(() => import("./pages/HotelOnboarding"));
const HotelSignup = lazy(() => import("./pages/HotelSignup"));
const HotelSetup = lazy(() => import("./pages/HotelSetup"));
const HotelOCR = lazy(() => import("./pages/HotelOCR"));
const HotelPreview = lazy(() => import("./pages/HotelPreview"));
const HotelRoomsPage = lazy(() => import("./pages/HotelRoomsPage"));
const HotelZonesPage = lazy(() => import("./pages/HotelZonesPage"));
const HotelLinktreePage = lazy(() => import("./pages/HotelLinktreePage"));
const CampingSetup = lazy(() => import("./pages/CampingSetup"));
const CampingDashboard = lazy(() => import("./pages/CampingDashboard"));
const CampingPreview = lazy(() => import("./pages/CampingPreview"));
const CampingOnboarding = lazy(() => import("./pages/CampingOnboarding"));
const CampingSignup = lazy(() => import("./pages/CampingSignup"));
const CampingOCR = lazy(() => import("./pages/CampingOCR"));
const CampingPitchesPage = lazy(() => import("./pages/CampingPitchesPage"));
const CampingZonesPage = lazy(() => import("./pages/CampingZonesPage"));
const CampingLinktreePage = lazy(() => import("./pages/CampingLinktreePage"));
const CampingAnalytics = lazy(() => import("./pages/CampingAnalytics"));
const CampingMenuCreation = lazy(() => import("./pages/CampingMenuCreation"));
const CampingLinktree = lazy(() => import("./pages/CampingLinktree"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <LoadingLogo />
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <RestaurantProvider>
            <Toaster />
            <BrowserRouter>
            {/* TawkTo chat removed */}
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Main routes */}
                <Route path="/" element={<SplitChoicePage />} />
                <Route path="/restaurants" element={<Test5 />} />
                <Route path="/lp-ocr" element={<LPOCRLanding />} />
                <Route path="/conversion" element={<ConversionLanding />} />
                <Route path="/quick-ocr" element={<QuickOCROnboarding />} />
                <Route path="/owner-landing" element={<RestaurantOwnerLanding />} />
                <Route path="/old-landing" element={<NewLanding />} />
                <Route path="/signup" element={<RapidOnboarding />} />
                <Route path="/ultra-fast-onboarding" element={<UltraFastOnboarding />} />
                <Route path="/auth" element={<SimpleAuth />} />
                <Route path="/quick-signup" element={<QuickSignup />} />
                <Route path="/setup-restaurant" element={<SetupRestaurant />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/onboarding/add-restaurant" element={<AddRestaurant />} />
                <Route path="/dashboard" element={<MainDashboard />} />
                <Route path="/hotel-dashboard" element={<HotelDashboard />} />
                <Route path="/hotel/analytics" element={<HotelAnalytics />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/hotel/menu/new" element={<HotelMenuCreation />} />
                <Route path="/hotel/:slug" element={<HotelLinktree />} />

                {/* Business features - accessible from dashboard */}
                <Route path="/dashboard/menus" element={<MenusPage />} />
                <Route path="/dashboard/menus/:id/edit" element={<MenuEditor />} />
                <Route path="/dashboard/menus/new" element={<MenuWizard />} />
                <Route path="/dashboard/create-menu" element={<MenuWizard />} />
                <Route path="/dashboard/menu-ocr" element={<MenuOCR />} />
                <Route path="/dashboard/ocr-menu-setup" element={<OCRMenuSetup />} />
                
                {/* OCR onboarding */}
                <Route path="/demo-ocr" element={<DemoOCR />} />
                <Route path="/dashboard/happy-hour" element={<HappyHour />} />
                <Route path="/dashboard/analytics" element={<Analytics />} />
                <Route path="/dashboard/qr-codes" element={<QrCodes />} />
                <Route path="/dashboard/reservations" element={<Reservations />} />
                <Route path="/dashboard-hotel/reservations" element={<Reservations />} />
                <Route path="/dashboard-camping/reservations" element={<Reservations />} />
                <Route path="/dashboard/restaurant-settings" element={<RestaurantSettings />} />
                <Route path="/hotel-dashboard/hotel-settings" element={<HotelSettings />} />
                <Route path="/camping-dashboard/camping-settings" element={<CampingSettings />} />
                <Route path="/dashboard/marketing" element={<Marketing />} />
                <Route path="/dashboard/translation" element={<Translation />} />
                {/* Public menu view */}
                <Route path="/menu/:slug" element={<MenuView />} />
                <Route path="/menu/shared/:token" element={<SharedMenuView />} />
                
                {/* Site one-page */}
                <Route path="/site/:slug" element={<RestaurantSite />} />
                
                {/* Wheel of fortune prize claim */}
                <Route path="/claim-prize/:token" element={<ClaimPrize />} />
                
                {/* Reservation cancellation */}
                <Route path="/cancel-reservation" element={<CancelReservation />} />
                <Route path="/confirm-reservation" element={<ConfirmReservation />} />
                <Route path="/reservation/manage/:token" element={<ManageReservation />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/activate-help" element={<ActivateHelp />} />
                <Route path="/review/:token" element={<ReviewPage />} />
                <Route path="/claim-account" element={<ClaimAccount />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                
                {/* Menu activation flow */}
                <Route path="/menu-preview" element={<MenuPreview />} />
                <Route path="/menu-preview/:id" element={<MenuPreview />} />
                <Route path="/success" element={<Success />} />

                {/* Marketing pages */}
                <Route path="/features" element={<Features />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/solutions/small-restaurants" element={<SmallRestaurants />} />
                <Route path="/solutions/chains" element={<Chains />} />
                <Route path="/solutions/fine-dining" element={<FineDining />} />
                <Route path="/solutions/cafes" element={<CafesAndBars />} />
                <Route path="/pizza" element={<Pizza />} />
                <Route path="/bar" element={<Bar />} />

                {/* Restaurant Map */}
                <Route path="/restaurants" element={<RestaurantMap />} />
                <Route path="/carte" element={<RestaurantMap />} />

                {/* Support */}
                <Route path="/support" element={<Support />} />
                <Route path="/dashboard/support" element={<SupportTickets />} />
                <Route path="/documentation" element={<Documentation />} />

                {/* Legal */}
                <Route path="/terms" element={<Terms />} />
                <Route path="/cgv" element={<Cgv />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/guide-google-maps" element={<GuideGoogleMaps />} />

                {/* Legacy routes */}
                <Route path="/old" element={<Index />} />
                <Route path="/payment-required" element={<PaymentRequired />} />
                
                {/* Gemini Landing */}
                <Route path="/gemini" element={<Gemini />} />
                
                {/* Magic Onboarding */}
                <Route path="/magic" element={<MagicOnboarding />} />
                
                {/* Landing V2 */}
                <Route path="/landing-v2" element={<LandingV2 />} />
                
                {/* Old SaasShowcase */}
                <Route path="/saas" element={<SaasShowcase />} />
                <Route path="/lp-copy" element={<LandingDuplicate />} />
                <Route path="/hotels" element={<HotelsLanding />} />
                <Route path="/campings" element={<CampingsLanding />} />
                <Route path="/hotel-onboarding" element={<HotelOnboarding />} />
                <Route path="/hotel-signup" element={<HotelSignup />} />
                <Route path="/hotel-setup" element={<HotelSetup />} />
                <Route path="/hotel-ocr" element={<HotelOCR />} />
                <Route path="/hotel-preview" element={<HotelPreview />} />
                <Route path="/hotel/rooms" element={<HotelRoomsPage />} />
                <Route path="/hotel/zones" element={<HotelZonesPage />} />
                <Route path="/hotel/linktree" element={<HotelLinktreePage />} />
                <Route path="/camping-setup" element={<CampingSetup />} />
                <Route path="/camping-dashboard" element={<CampingDashboard />} />
                <Route path="/camping-preview" element={<CampingPreview />} />
                <Route path="/camping-onboarding" element={<CampingOnboarding />} />
                <Route path="/camping-signup" element={<CampingSignup />} />
                <Route path="/camping-ocr" element={<CampingOCR />} />
                <Route path="/camping/pitches" element={<CampingPitchesPage />} />
                <Route path="/camping/zones" element={<CampingZonesPage />} />
                <Route path="/camping/linktree" element={<CampingLinktreePage />} />
                <Route path="/camping/analytics" element={<CampingAnalytics />} />
                <Route path="/camping/menu/new" element={<CampingMenuCreation />} />
                <Route path="/camping/:slug" element={<CampingLinktree />} />

                {/* Admin Panel */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="funnel" element={<AdminFunnel />} />
                  <Route path="leads" element={<AdminLeadsCRM />} />
                  <Route path="leads/import" element={<AdminLeadsImport />} />
                  <Route path="ocr" element={<AdminOCR />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="tickets" element={<AdminTickets />} />
                  <Route path="support" element={<AdminSupport />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <CookieConsent />
          </BrowserRouter>
          </RestaurantProvider>
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
